import { Flag } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { CampaignSchema } from "@/types";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatEndDate } from "@/utils/format-end-date";

/**
 * Sidebar card for finished campaigns (Figma 5086:1740) — the finished
 * counterpart of CampaignJoinCta: a centred "{type} finished" row in the
 * disabled palette. Takes the join CTA's slot at the top of the sidebar.
 */
export const CampaignFinishedCard = ({
  campaign,
  title,
  className,
}: {
  campaign: CampaignSchema;
  /** Translated heading, e.g. "Tournament finished" / "Prize drops finished". */
  title: string;
  className?: string;
}) => {
  const { t } = useTranslation();
  const ended = formatEndDate(campaign.end);
  return (
    <Card
      inset="none"
      className={cn(
        // Tighter paddings on mobile, where the card sits inline under the tabs.
        "flex items-center justify-center gap-2.5 p-3 lg:gap-3 lg:p-4",
        className
      )}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-disabled-bg/40 lg:size-9">
        <Flag
          className="size-4 text-muted-foreground lg:size-4.5"
          strokeWidth={2}
        />
      </span>
      <div className="flex min-w-0 flex-col">
        <span className="font-heading text-[13px] leading-4.5 font-bold text-muted-foreground lg:text-[14px]">
          {title}
        </span>
        {ended && (
          <span className="font-body text-[12px] text-muted-foreground/70">
            {t("campaign.endedOn", "Ended {{date}}", { date: ended })}
          </span>
        )}
      </div>
    </Card>
  );
};
